/**
 * Google アカウントの紐付け（切り替え）の途中経過。
 *
 * linkIdentity は Google の画面へ移動してから /auth/callback に戻ってくるので、
 * 「何を置き換える手続きだったか」と「失敗して戻ってきたか」をその間だけ覚えておく。
 * 戻り先を専用の URL にしないのは、Supabase の Redirect URLs 許可リストを増やさずに済ませるため。
 *
 * タブを閉じれば消えてよい情報なので sessionStorage に置く。使えない環境では何もしない
 * （覚えられなくても紐付け自体は Supabase 側で終わっており、設定画面で状態は見られる）。
 */

const KEY = 'tfb-google-link-pending'

export type PendingGoogleLink = {
  /** 外す予定の古い Google の identity_id。新しく足すだけなら null */
  replaceIdentityId: string | null
  /** Google 側・Supabase 側から返ってきた失敗の理由（error_code） */
  error: string | null
}

export function savePendingGoogleLink(input: { replaceIdentityId: string | null }): void {
  write({ replaceIdentityId: input.replaceIdentityId, error: null })
}

/** 手続き中のときだけ失敗を記録する。通常のログインの失敗とは混ぜない */
export function recordGoogleLinkError(errorCode: string): void {
  const pending = loadPendingGoogleLink()
  if (pending) write({ ...pending, error: errorCode })
}

export function loadPendingGoogleLink(): PendingGoogleLink | null {
  try {
    const raw = sessionStorage.getItem(KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    return isPendingGoogleLink(parsed) ? parsed : null
  } catch {
    return null
  }
}

export function clearPendingGoogleLink(): void {
  try {
    sessionStorage.removeItem(KEY)
  } catch {
    // 保存できない環境では最初から何も残っていない
  }
}

function write(value: PendingGoogleLink): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(value))
  } catch {
    // プライベートブラウズ等で保存できない。紐付け自体は続けられる（上のコメント参照）
  }
}

function isPendingGoogleLink(value: unknown): value is PendingGoogleLink {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  const isNullableString = (x: unknown) => x === null || typeof x === 'string'
  return isNullableString(v.replaceIdentityId) && isNullableString(v.error)
}
